import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Table, Text } from '@gravity-ui/uikit';
import type { FinanceSummaryResponse, SubscriptionState } from '@odonix/contracts';
import { useFinanceApi } from '../../lib/api/finance-context.js';
import { useTenantsApi } from '../../lib/api/context.js';
import { count, moment, rub, rubCompact, rubSigned } from '../../ui/format.js';
import { MetricStrip } from '../../ui/metric-strip.js';
import { MonthChart } from '../../ui/month-chart.js';
import { Page, Section } from '../../ui/page.js';
import { PeriodPicker } from '../../ui/period-picker.js';
import { periodOf, periodQuery } from '../../ui/period.js';
import { RouterLink, useGo } from '../../ui/router-link.js';
import { Empty, ErrorNote, LoadingRows } from '../../ui/states.js';

/**
 * Обзор — деньги платформы за период и состояние базы клиник.
 *
 * Сверху то, ради чего экран открывают: сколько пришло, сколько ушло и что
 * осталось. Ниже — год по месяцам, куда ушли деньги и кому из менеджеров, и
 * состояние подписок, где каждое число ведёт в отфильтрованный список.
 */

const routeApi = getRouteApi('/shell/');

const STATE_LINK: { state: SubscriptionState; label: string }[] = [
  { state: 'active', label: 'Активных' },
  { state: 'trial', label: 'На пробном периоде' },
  { state: 'grace', label: 'Ждут оплаты' },
  { state: 'restricted', label: 'С закрытой записью' },
  { state: 'suspended', label: 'Приостановлено' },
];

export function HomeScreen() {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const period = periodOf(search);
  const finance = useFinanceApi();
  const tenants = useTenantsApi();

  const summary = useQuery({
    queryKey: ['finance-summary', period.from, period.to],
    queryFn: () => finance.summary(period),
    placeholderData: (previous) => previous,
  });
  const dashboard = useQuery({ queryKey: ['dashboard'], queryFn: () => tenants.dashboard() });

  const data = summary.data;

  return (
    <Page
      title="Обзор"
      actions={
        <PeriodPicker
          value={period}
          onChange={(next) => {
            void navigate({ search: { from: next.from, to: next.to } });
          }}
        />
      }
    >
      {summary.isError ? (
        <ErrorNote title="Сводка денег не загрузилась" error={summary.error} onRetry={() => void summary.refetch()} />
      ) : (
        <MetricStrip
          loading={data === undefined}
          metrics={[
            {
              label: 'Пришло',
              value: rub(data?.incomeMinor ?? 0),
              hint: data === undefined ? undefined : `${count(data.paymentsCount)} ${data.paymentsCount === 1 ? 'оплата' : 'оплат'} подписок`,
              tone: 'positive',
            },
            {
              label: 'Ушло',
              value: rub(data?.outgoingMinor ?? 0),
              hint: 'расходы, проценты, премии и возвраты',
            },
            {
              label: 'Итог за период',
              value: rubSigned(data?.netMinor ?? 0),
              tone: (data?.netMinor ?? 0) < 0 ? 'danger' : 'primary',
            },
          ]}
        />
      )}

      <Section title="Деньги по месяцам">
        {data === undefined ? (
          <LoadingRows rows={1} height={240} />
        ) : (
          <MonthChart
            title="Деньги по месяцам"
            months={data.months.map((row) => row.month)}
            series={[
              {
                key: 'income',
                label: 'Пришло',
                color: 'var(--g-color-base-positive-heavy)',
                values: data.months.map((row) => row.incomeMinor),
              },
              {
                key: 'outgoing',
                label: 'Ушло',
                color: 'var(--g-color-base-brand)',
                values: data.months.map(
                  (row) => row.expensesMinor + row.commissionsMinor + row.bonusesMinor + row.refundsMinor,
                ),
              },
            ]}
            line={{
              key: 'net',
              label: 'Итог',
              color: 'var(--g-color-text-primary)',
              values: data.months.map((row) => row.netMinor),
            }}
            format={rub}
            formatAxis={rubCompact}
          />
        )}
      </Section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Section
          title="Куда ушли деньги"
          actions={<RouterLink to={`/expenses?${periodQuery(period)}`}>Все расходы</RouterLink>}
        >
          {data === undefined ? <LoadingRows rows={4} height={28} /> : <Outgoing data={data} />}
        </Section>

        <Section title="Менеджеры" actions={<RouterLink to={`/managers?${periodQuery(period)}`}>Все менеджеры</RouterLink>}>
          {data === undefined ? <LoadingRows rows={3} height={28} /> : <ManagersBreakdown data={data} />}
        </Section>
      </div>

      <Section title="Состояние подписок">
        {dashboard.isError ? (
          <ErrorNote title="Подписки не загрузились" error={dashboard.error} onRetry={() => void dashboard.refetch()} />
        ) : dashboard.data === undefined ? (
          <LoadingRows rows={2} height={32} />
        ) : (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap gap-x-10 gap-y-3">
              <Figure label="MRR" value={rub(dashboard.data.mrrMinor)} hint="ожидаем в текущем цикле" />
              <Figure label="Новых в этом месяце" value={count(dashboard.data.newTenantsThisMonth)} />
              <Figure label="Пробный кончается за 3 дня" value={count(dashboard.data.trialEndingSoon)} />
              <Figure
                label="Клиник и лабораторий"
                value={`${count(dashboard.data.tenantsByKind.clinic)} · ${count(dashboard.data.tenantsByKind.lab)}`}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {STATE_LINK.map((item) => (
                <RouterLink key={item.state} to={`/tenants?state=${item.state}`}>
                  {`${item.label} ${count(dashboard.data.tenantsByState[item.state])}`}
                </RouterLink>
              ))}
            </div>
          </div>
        )}
      </Section>

      <Section title="Последние операции" flush>
        {data === undefined ? (
          <div className="px-5 pb-3">
            <LoadingRows rows={5} height={32} />
          </div>
        ) : data.recent.length === 0 ? (
          <div className="px-5">
            <Empty title="За период операций не было" description="Ни оплат, ни расходов, ни премий в выбранные даты." />
          </div>
        ) : (
          <Operations data={data} />
        )}
      </Section>
    </Page>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Text color="secondary">{label}</Text>
      <Text variant="subheader-3" className="tabular">
        {value}
      </Text>
      {hint === undefined ? null : (
        <Text variant="caption-2" color="secondary">
          {hint}
        </Text>
      )}
    </div>
  );
}

function Bar({ label, amount, total, to }: { label: string; amount: number; total: number; to?: string }) {
  const share = total === 0 ? 0 : Math.max(amount / total, amount > 0 ? 0.02 : 0);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        {to === undefined ? <Text>{label}</Text> : <RouterLink to={to}>{label}</RouterLink>}
        <Text className="tabular" whiteSpace="nowrap">
          {rub(amount)}
        </Text>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-[var(--g-color-base-generic)]">
        <div className="h-full rounded-full bg-[var(--g-color-base-brand)]" style={{ width: `${String(share * 100)}%` }} />
      </div>
    </div>
  );
}

function Outgoing({ data }: { data: FinanceSummaryResponse }) {
  const rows = [
    ...data.expensesByCategory.map((row) => ({
      key: row.categoryId,
      label: row.name,
      amount: row.amountMinor,
      to: `/expenses?${periodQuery(data.period)}&categoryId=${row.categoryId}`,
    })),
    { key: 'commissions', label: 'Проценты менеджерам', amount: data.commissionsMinor, to: undefined },
    { key: 'bonuses', label: 'Премии менеджерам', amount: data.bonusesMinor, to: undefined },
    { key: 'refunds', label: 'Возвраты клиникам', amount: data.refundsMinor, to: undefined },
  ].filter((row) => row.amount > 0);

  if (rows.length === 0) {
    return <Empty title="За период ничего не ушло" description="Расходы заводятся в разделе «Расходы»." />;
  }
  return (
    <div className="flex flex-col gap-4">
      {rows
        .sort((a, b) => b.amount - a.amount)
        .map((row) => (
          <Bar key={row.key} label={row.label} amount={row.amount} total={data.outgoingMinor} {...(row.to === undefined ? {} : { to: row.to })} />
        ))}
    </div>
  );
}

function ManagersBreakdown({ data }: { data: FinanceSummaryResponse }) {
  if (data.managers.length === 0) {
    return (
      <Empty
        title="Начислений менеджерам нет"
        description="Процент начисляется с оплат клиник, закреплённых за менеджером; премии назначаются в его карточке."
      />
    );
  }
  return (
    <div className="flex flex-col divide-y divide-[var(--g-color-line-generic)]">
      {data.managers.map((row) => (
        <div key={row.managerId} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5">
          <RouterLink to={`/managers/${row.managerId}?${periodQuery(data.period)}`}>{row.fullName}</RouterLink>
          <div className="flex items-baseline gap-4">
            <Text color="secondary" className="tabular" whiteSpace="nowrap">
              оплаты {rubCompact(row.revenueMinor)}
            </Text>
            <Text className="tabular" whiteSpace="nowrap">
              к выплате {rub(row.commissionMinor + row.bonusesMinor)}
            </Text>
          </div>
        </div>
      ))}
    </div>
  );
}

const OPERATION_KIND: Record<FinanceSummaryResponse['recent'][number]['kind'], string> = {
  payment: 'Оплата',
  refund: 'Возврат',
  expense: 'Расход',
  bonus: 'Премия',
};

function Operations({ data }: { data: FinanceSummaryResponse }) {
  const go = useGo();
  return (
    <Table
      className="w-full"
      edgePadding
      data={data.recent}
      getRowDescriptor={(row) => ({ id: `${row.kind}-${row.id}`, interactive: row.tenantId !== null })}
      onRowClick={(row) => {
        if (row.tenantId !== null) go(`/tenants/${row.tenantId}`);
      }}
      columns={[
        { id: 'at', name: 'Когда', template: (row) => <span className="tabular">{moment(row.at)}</span>, width: 190 },
        { id: 'kind', name: 'Что', template: (row) => OPERATION_KIND[row.kind], width: 110 },
        { id: 'title', name: 'Описание', template: (row) => row.title },
        {
          id: 'amount',
          name: 'Сумма',
          align: 'end',
          template: (row) => (
            <span className={`tabular whitespace-nowrap ${row.amountMinor < 0 ? 'money-minus' : 'money-plus'}`}>
              {rubSigned(row.amountMinor)}
            </span>
          ),
        },
      ]}
    />
  );
}

